import type { components, operations } from '@connected/api-client/api.generated';
import type { ValidationError } from '@tanstack/react-form';
import type { ZodValidator } from '@tanstack/zod-form-adapter';

import { api, client } from '@connected/api-client';
import { validators } from '@connected/api-client/generated/validatorsv2.generated';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import FieldInfo from '@connected/form-fields/components/field-info';
import { Label } from '@connected/form-fields/components/label';
import { NumberField } from '@connected/form-fields/components/number';
import {
    MultipleOptionField,
    MultipleOptionItem,
    SingleCheckOptionItem,
    SingleOptionField,
} from '@connected/form-fields/components/option';
import {
    Button,
} from '@connected/ui/components/button';
import {
    Input,
} from '@connected/ui/components/input';
import {
    Spinner,
} from '@connected/ui/components/spinner';
import { useForm } from '@tanstack/react-form';
import { zodValidator } from '@tanstack/zod-form-adapter';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import z from 'zod';

import { queryClient } from '@/lib/query-client';
import { getFormErrors, placeFormErrors } from '@/lib/utils';
import { useOffline } from '@/stores/network-status';

type KVKFormType = 'kvkNumber' | 'name' | 'postalCode + houseNumber';

type KVKFormValues = {
    selectedKVKNumber: string;
    type: KVKFormType[];
    kvkNumber: string;
    name: string;
    postalCode: string;
    houseNumber: number | null;
};

type KVKStatusType = null | 'SEARCHING' | 'REQUEST_INCOMPLETE' | 'NOT_FOUND' | 'SUCCESS';

type ManualCreateValues = { name: string; address: { street: string; streetNumber: string; postalCode: string; locality: string } };

type OrganisationRoles = {
    isProjectCustomer: boolean;
    isObjectOwner: boolean;
    isObjectAdministrator: boolean;
    isObjectTenant: boolean;
    isPVInstallationConstructionCompany?: boolean;
};

export function SearchOrganization({
    params,
    organisationRoles,
}: {
    params: { projectVersionId: string };
    organisationRoles: OrganisationRoles;
}) {
    const [createMethod, setCreateMethod] = useState<'kvk' | 'manual'>('kvk');
    const { t } = useTranslation('common');

    return (
        <div className="flex flex-col gap-4">
            {
                createMethod === 'kvk'
                    ? (
                            <KVKSearch
                                electricalProjectVersionId={params.projectVersionId}
                                organisationRoles={organisationRoles}
                            />
                        )
                    : (
                            <ManualCreate
                                electricalProjectVersionId={params.projectVersionId}
                                organisationRoles={organisationRoles}
                            />
                        )
            }
            <hr />
            {createMethod === 'kvk'
                ? (
                        <span>
                            {t('search.not_found_looking_for')}
                            {' '}
                            <Button variant="link" className="text-text-primary self-start p-0 underline" onClick={() => setCreateMethod('manual')}>
                                {t('search.add_manually')}
                            </Button>
                        </span>

                    )
                : (
                        <span>
                            {t('search.want_to_search')}
                            {' '}
                            <Button variant="link" className="text-text-primary p-0 underline" onClick={() => setCreateMethod('kvk')}>
                                {t('search.use_search_function')}
                            </Button>
                        </span>
                    )}
        </div>

    );
}

type KVKSearchProps = {
    electricalProjectVersionId: string;
    organisationRoles: OrganisationRoles;
};

function KVKSearch({
    electricalProjectVersionId,
    organisationRoles,
}: KVKSearchProps) {
    const isUserOffline = useOffline();
    const { t } = useTranslation('applications');
    const { t: c } = useTranslation('common');

    const [kvkSearch, setKVKsearch] = useState<components['schemas']['ListKVKRegistrationQueryResultRegistration'][]>([]);

    const [kvkOptions, setKvkOptions] = useState<
        { label: string; value: string }[]
    >([]);

    const [status, setStatus] = useState<KVKStatusType>(null);

    const createOrganizationMutation = api.useMutation(
        'post',
        '/api/v2/electrical/projects/organizations/create',
        {
            onSuccess: () => {
                toast.success('Organizatie aangemaakt');
                queryClient.invalidateQueries(
                    elektraAppQueryOptions.project.request.organizationOptions({ electricalProjectVersionId }),
                );
                setKvkOptions([]);
                setStatus(null);
            },
            onError: () => {
                toast.error(c('messages.toast.organizationCreateError'));
            },
        },
    );

    async function handleCreate({ kvkNummer }: { kvkNummer: string }) {
        const organizationName = kvkSearch.find(
            x => x.KVKNumber === kvkNummer,
        )?.Name;

        if (!organizationName) {
            toast.error(c('messages.toast.organizationCreateError'));
            return;
        }
        await createOrganizationMutation.mutateAsync({
            body: {
                ElectricalProjectVersionId: electricalProjectVersionId,
                BusinessRegistration: {
                    NLKvk: {
                        KVKNumber: kvkNummer,
                    },
                    SourceKind: 'NLKVK',
                },
                IsProjectCustomer: organisationRoles.isProjectCustomer,
                IsObjectAdministrator: organisationRoles.isObjectAdministrator,
                IsObjectOwner: organisationRoles.isObjectOwner,
                IsObjectTenant: organisationRoles.isObjectTenant,
                IsPVInstallationConstructionCompany: organisationRoles.isPVInstallationConstructionCompany ?? false,
                LegalPersonKind: 'JuridicalPerson',
                Name: organizationName,
            },
        });
    }

    const KVKField = useForm<KVKFormValues, ZodValidator>({
        defaultValues: {
            selectedKVKNumber: '',
            type: ['name'],
            kvkNumber: '',
            name: '',
            postalCode: '',
            houseNumber: null,
        },
        validatorAdapter: zodValidator(),
        validators: {
            onBlur: z.object({
                type: z.array(z.string()),
                kvkNumber: validators.listKVKRegistrationQuery.kVKNumber,
                name: validators.listKVKRegistrationQuery.name,
                postalCode: validators.listKVKRegistrationQuery.postalCode,
                houseNumber: validators.listKVKRegistrationQuery.streetNumber,
            }).refine((data) => {
                // If name is selected, it must be filled
                if (data.type.includes('name')) {
                    return data.name && data.name.trim() !== '';
                }
                return true;
            }, {
                message: 'Bedrijfsnaam is verplicht',
                path: ['name'],
            }).refine((data) => {
                // If postalCode + houseNumber is selected, both must be filled
                if (data.type.includes('postalCode + houseNumber')) {
                    const hasPostalCode = data.postalCode && data.postalCode.trim() !== '';
                    const hasHouseNumber = data.houseNumber !== null;
                    return hasPostalCode && hasHouseNumber;
                }
                return true;
            }, {
                message: 'Postcode en huisnummer zijn verplicht',
                path: ['houseNumber'],
            }),
        },
    });

    async function getKVK() {
        setKvkOptions([]);
        setStatus('SEARCHING');

        const type = KVKField.getFieldValue('type');

        const name = KVKField.getFieldValue('name');
        const kvkNumber = KVKField.getFieldValue('kvkNumber');
        const postalCode = KVKField.getFieldValue('postalCode');
        const houseNumber = KVKField.getFieldValue('houseNumber');

        const query: operations['ListKVKRegistrationQuery_Get']['parameters']['query']
            = {
                PageNumber: 1,
                PageSize: 100,
                ObjectKind: 'ElectricalProject',
                ObjectId: electricalProjectVersionId,
            };

        let errors: ValidationError[] = [];

        if (type.includes('name')) {
            errors = [...errors, ...await KVKField.validateField('name', 'submit')];
            query.Name = name;
        }

        if (type.includes('kvkNumber')) {
            errors = [...errors, ...await KVKField.validateField('kvkNumber', 'submit')];
            query.KVKNumber = kvkNumber;
        }

        if (type.includes('postalCode + houseNumber')) {
            errors = [...errors, ...await KVKField.validateField('postalCode', 'submit'), ...await KVKField.validateField('houseNumber', 'submit')];
            query.PostalCode = postalCode;
            query.StreetNumber = houseNumber;

            if (errors.length === 0) {
                if (!postalCode) {
                    errors.push('Postcode is verplicht');
                    KVKField.fieldInfo.postalCode.instance?.setErrorMap({
                        onSubmit: 'Postcode is verplicht',
                    });
                }

                if (houseNumber === null) {
                    errors.push('Huisnummer is verplicht');
                    KVKField.fieldInfo.houseNumber.instance?.setErrorMap({
                        onSubmit: 'Huisnummer is verplicht',
                    });
                }
            }
        }

        if (errors.length > 0) {
            setStatus(null);
            return;
        }

        try {
            const { data, error } = await client.GET(
                '/api/v2/chamberofcommerce/nl/kvk/registrations/list',
                {
                    params: {
                        query,
                    },
                },
            );

            if (error) {
                return setStatus('REQUEST_INCOMPLETE');
            }

            if (!data?.Registrations?.length) {
                console.warn('No KVK registrations found', data);
                return setStatus('NOT_FOUND');
            }

            // const kvkRegistrations = data?.Registrations
            //     ?.filter(
            //         x =>
            //             x.Type === 'rechtspersoon'
            //             && !organisationData?.Organizations?.some(
            //                 org => org.BusinessRegistration?.Number === x.KVKNumber,
            //             ),
            //     );

            setKVKsearch(data?.Registrations ?? []);

            const kvkRegistrationsOptions = data?.Registrations?.map((registration) => {
                const name = registration?.Name || '';
                const street = registration?.DomesticAddress?.Street || '';
                const streetNumber = registration?.DomesticAddress?.StreetNumber || '';
                const postalCode = registration?.DomesticAddress?.PostalCode || '';
                const locality = registration?.DomesticAddress?.Locality || '';

                const address = `${[street, streetNumber].filter(Boolean).join(' ')}${(street || streetNumber) && ', '}${[postalCode, locality].filter(Boolean).join(' ')}`;
                const label = `${name} - ${registration?.Type} ${address && ` (${address})`}`;

                return {
                    label,
                    value: registration.KVKNumber!,
                };
            }) || [];

            setKvkOptions(kvkRegistrationsOptions);

            if (kvkRegistrationsOptions.length === 0) {
                setStatus('NOT_FOUND');
                return toast.error(
                    t('those-involved.client.form.kvk.status.not_found'),
                );
            }

            return setStatus('SUCCESS');
        }
        catch {
            return setStatus('REQUEST_INCOMPLETE');
        }
    }

    return (
        <div className="grid gap-4">
            <KVKField.Field name="type">
                {({ handleChange, state, name }) => (
                    <Label label="Zoek KVK op:" htmlFor="type">
                        <MultipleOptionField
                            name={name}
                            handleChange={(e) => {
                                const selectedItems
                                    = KVKField.getFieldValue('type');

                                selectedItems.forEach((item) => {
                                    if (!e.includes(item)) {
                                        if (
                                            item
                                            === 'postalCode + houseNumber'
                                        ) {
                                            KVKField.setFieldValue(
                                                'postalCode',
                                                '',
                                            );
                                            KVKField.setFieldValue(
                                                'houseNumber',
                                                null,
                                            );
                                        }
                                        if (item === 'name') {
                                            KVKField.setFieldValue(
                                                'name',
                                                '',
                                            );
                                        }
                                        if (item === 'kvkNumber') {
                                            KVKField.setFieldValue(
                                                'kvkNumber',
                                                '',
                                            );
                                        }
                                    }
                                });

                                handleChange(e);
                            }}
                            className="flex flex-row gap-2"
                            state={state}
                            options={[
                                {
                                    label: 'KVK nummer',
                                    value: 'kvkNumber',
                                },
                                {
                                    label: 'Bedrijfsnaam',
                                    value: 'name',
                                },
                                {
                                    label: 'Postcode/huisnummer',
                                    value: 'postalCode + houseNumber',
                                },
                            ]}
                            render={MultipleOptionItem}
                            disabled={isUserOffline}

                        />

                        <FieldInfo state={state} />
                    </Label>
                )}
            </KVKField.Field>

            <KVKField.Subscribe
                selector={state => [state.values.type]}
            >
                {([type]) =>
                    type.includes('kvkNumber') && (
                        <KVKField.Field
                            name="kvkNumber"
                        >
                            {({ handleChange, state, name }) => (
                                <Label label="KVK nummer">
                                    <Input
                                        name={name}
                                        value={state.value}
                                        onChange={(e) => {
                                            handleChange(
                                                e.target.value,
                                            );
                                        }}
                                        placeholder="Noteer KVK nummer"
                                        disabled={isUserOffline}

                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </KVKField.Field>
                    )}
            </KVKField.Subscribe>

            <KVKField.Subscribe
                selector={state => [state.values.type]}
            >
                {([type]) =>
                    type.includes('name') && (
                        <KVKField.Field
                            name="name"
                        >
                            {({ handleChange, state, name }) => (
                                <Label label="Bedrijfsnaam">
                                    <Input
                                        name={name}
                                        value={state.value}
                                        onChange={(e) => {
                                            handleChange(
                                                e.target.value,
                                            );
                                        }}
                                        placeholder="Noteer bedrijfsnaam"
                                        disabled={isUserOffline}

                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </KVKField.Field>
                    )}
            </KVKField.Subscribe>

            <KVKField.Subscribe
                selector={state => [state.values.type]}
            >
                {([type]) =>
                    type.includes('postalCode + houseNumber') && (
                        <div className="grid grid-cols-2 gap-4">
                            <KVKField.Field
                                name="postalCode"
                            >
                                {({ handleChange, state, name }) => (
                                    <Label label="Postcode">
                                        <Input
                                            name={name}
                                            value={state.value}
                                            onChange={(e) => {
                                                handleChange(
                                                    e.target.value,
                                                );
                                            }}
                                            placeholder="Noteer postcode (bijvoorbeeld 1234AB)"
                                            disabled={isUserOffline}

                                        />
                                        <FieldInfo state={state} />
                                    </Label>
                                )}
                            </KVKField.Field>
                            <KVKField.Field
                                name="houseNumber"
                            >
                                {({ handleChange, state, name }) => (
                                    <Label label="Huisnummer">
                                        <NumberField
                                            name={name}
                                            value={state.value}
                                            onChange={(e) => {
                                                handleChange(e);
                                                KVKField.handleSubmit();
                                            }}
                                            placeholder="Noteer huisnummer"
                                            disabled={isUserOffline}

                                        />
                                        <FieldInfo state={state} />
                                    </Label>
                                )}
                            </KVKField.Field>
                        </div>
                    )}
            </KVKField.Subscribe>

            <Button
                className="w-full"
                onClick={getKVK}
                disabled={status === 'SEARCHING' || isUserOffline} // Aan het zoeken
            >
                {(status === 'SEARCHING' || createOrganizationMutation.isPending) && (
                    <Spinner />
                )}
                Zoeken
            </Button>
            {
                status != null && status !== 'SEARCHING'
                && (
                    kvkOptions.length > 0
                        ? (
                                <KVKField.Field name="selectedKVKNumber">
                                    {({ state, name, handleChange }) => (
                                        <Label label="Bedrijf">
                                            <SingleOptionField
                                                name={name}
                                                handleChange={(e) => {
                                                    handleCreate({
                                                        kvkNummer: e,
                                                    });
                                                    handleChange(e);
                                                }}
                                                state={state}
                                                options={kvkOptions}
                                                render={
                                                    SingleCheckOptionItem
                                                }
                                                dropdown
                                                closeOnClick
                                                placeholder="Selecteer uw keuze:"
                                                disabled={
                                                    createOrganizationMutation.isPending
                                                }
                                                search
                                            />
                                        </Label>
                                    )}
                                </KVKField.Field>
                            )
                        : (
                            // show error message if no results & is not initial or searching
                                <em className="text-center text-red-500">
                                    {t(`those-involved.client.form.kvk.status.${status.toLowerCase()}`)}
                                </em>
                            )
                )

            }
        </div>
    );
}

type ManualCreateProps = {
    electricalProjectVersionId: string;
    organisationRoles: OrganisationRoles;
};

function ManualCreate({
    electricalProjectVersionId,
    organisationRoles,
}: ManualCreateProps) {
    const isUserOffline = useOffline();

    const createOrganizationMutation = api.useMutation(
        'post',
        '/api/v2/electrical/projects/organizations/create',
        {
            onSuccess: () => {
                toast.success('Organizatie aangemaakt');
                queryClient.invalidateQueries(
                    elektraAppQueryOptions.project.request.organizationOptions({ electricalProjectVersionId }),
                );
                // eslint-disable-next-line ts/no-use-before-define
                form.reset();
            },
            onError: (error) => {
                toast.error('Er is iets misgegaan bij het aanmaken van de organisatie');
                const errors = getFormErrors<ManualCreateValues>(error);

                // eslint-disable-next-line ts/no-use-before-define
                placeFormErrors({ fieldErrors: errors, formApi: form });
            },
        },
    );

    const form = useForm<ManualCreateValues, ZodValidator>({
        defaultValues: {
            name: '',
            address: {
                street: '',
                streetNumber: '',
                postalCode: '',
                locality: '',
            },
        },
        validatorAdapter: zodValidator(),
        onSubmit: async ({ value }) => {
            await createOrganizationMutation.mutateAsync({
                body: {
                    ElectricalProjectVersionId: electricalProjectVersionId,
                    IsProjectCustomer: organisationRoles.isProjectCustomer,
                    IsObjectAdministrator: organisationRoles.isObjectAdministrator,
                    IsObjectOwner: organisationRoles.isObjectOwner,
                    IsObjectTenant: organisationRoles.isObjectTenant,
                    IsPVInstallationConstructionCompany: organisationRoles.isPVInstallationConstructionCompany ?? false,
                    LegalPersonKind: 'NaturalPerson',
                    Name: value.name,
                    NaturalPersonAddress: {
                        Street: value.address.street,
                        StreetNumber: value.address.streetNumber,
                        PostalCode: value.address.postalCode,
                        Locality: value.address.locality,
                    },
                },
            });
        },
        validators: {
            onSubmit: z.object({
                name: validators.createElectricalProjectOrganizationCommand.name,
                address: z.object({
                    street: validators.createElectricalProjectOrganizationCommand.createElectricalProjectOrganizationCommandAddress.street,
                    streetNumber: validators.createElectricalProjectOrganizationCommand.createElectricalProjectOrganizationCommandAddress.streetNumber,
                    postalCode: validators.createElectricalProjectOrganizationCommand.createElectricalProjectOrganizationCommandAddress.postalCode,
                    locality: validators.createElectricalProjectOrganizationCommand.createElectricalProjectOrganizationCommandAddress.locality,
                }),
            }),
        },
    });

    return (
        <div className="grid gap-4">
            <form.Field name="name">
                {({ handleChange, state, name }) => (
                    <Label label="Naam">
                        <Input
                            name={name}
                            value={state.value}
                            onChange={(e) => {
                                handleChange(e.target.value);
                            }}
                            placeholder="Noteer naam"
                            disabled={createOrganizationMutation.isPending || isUserOffline}
                        />
                        <FieldInfo state={state} />
                    </Label>
                )}
            </form.Field>
            <div className="grid grid-cols-2 gap-4">
                <form.Field name="address.street">
                    {({ handleChange, state, name }) => (
                        <Label label="Straat">
                            <Input
                                name={name}
                                value={state.value}
                                onChange={(e) => {
                                    handleChange(e.target.value);
                                }}
                                placeholder="Noteer straat"
                                disabled={createOrganizationMutation.isPending || isUserOffline}
                            />
                            <FieldInfo state={state} />
                        </Label>
                    )}
                </form.Field>
                <form.Field name="address.streetNumber">
                    {({ handleChange, state, name }) => (
                        <Label label="Huisnummer">
                            <Input
                                name={name}
                                value={state.value}
                                onChange={(e) => {
                                    handleChange(e.target.value);
                                }}
                                placeholder="Noteer huisnummer"
                                disabled={createOrganizationMutation.isPending || isUserOffline}
                            />
                            <FieldInfo state={state} />
                        </Label>
                    )}
                </form.Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
                <form.Field name="address.postalCode">
                    {({ handleChange, state, name }) => (
                        <Label label="Postcode">
                            <Input
                                name={name}
                                value={state.value}
                                onChange={(e) => {
                                    handleChange(e.target.value);
                                }}
                                placeholder="Noteer postcode (bijvoorbeeld 1234AB)"
                                disabled={createOrganizationMutation.isPending || isUserOffline}
                            />
                            <FieldInfo state={state} />
                        </Label>
                    )}
                </form.Field>
                <form.Field name="address.locality">
                    {({ handleChange, state, name }) => (
                        <Label label="Plaats">
                            <Input
                                name={name}
                                value={state.value}
                                onChange={(e) => {
                                    handleChange(e.target.value);
                                }}
                                placeholder="Noteer plaats"
                                disabled={createOrganizationMutation.isPending || isUserOffline}
                            />
                            <FieldInfo state={state} />
                        </Label>
                    )}
                </form.Field>
            </div>
            <Button
                type="submit"
                className="w-full"
                disabled={createOrganizationMutation.isPending || isUserOffline}
                onClick={() => form.handleSubmit()}
            >
                {(createOrganizationMutation.isPending || isUserOffline) && (
                    <Spinner />
                )}
                Handmatig aanmaken
            </Button>
        </div>
    );
}
